import React, { useState, useEffect } from 'react';
import "./Revisar.css";
import { Button, Modal, Input, Table, Space, Descriptions, message } from 'antd';
import { FileTextOutlined, NumberOutlined, ArrowLeftOutlined, CalendarOutlined, TagOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import API_CONFIG from '../../../config/api.config';

const Revisar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const compraId = location.state?.compraId;

  const [fornecedor, setFornecedor] = useState({});
  const [itens, setItens] = useState([]);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [modalAprovarVisivel, setModalAprovarVisivel] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!compraId) {
      message.error('ID da compra não informado!');
      navigate('/adm/revisarcotacao');
      return;
    }
    setLoading(true);
    api.get(API_CONFIG.ENDPOINTS.COMPRAS.VER_ITENS_COMPRA(compraId))
      .then(response => {
        if (response.data && response.data.status && response.data.compras) {
          const compra = response.data.compras;
          setFornecedor({
            nome: compra.cotacao?.fornecedor?.FOR_RAZAO_SOCIAL || '---',
            veiculo: '', // Adapte se necessário
            dataAbertura: compra.COM_DATA_VINCULACAO,
            status: compra.cotacao?.COT_STATUS || '---',
          });
          setItens(
            (compra.itens || []).map(item => ({
              id: item.IT_ID,
              nome: item.IT_NOME,
              quantidade: Number(item.IT_QUANTIDADE),
              valorUnitario: Number(item.item_compra?.ITC_VALOR_ORIGINAL || 0)
            }))
          );
        } else {
          message.error('Compra não encontrada!');
          navigate('/adm/revisarcotacao');
        }
      })
      .catch(() => {
        message.error('Erro ao buscar detalhes da compra!');
        navigate('/adm/revisarcotacao');
      })
      .finally(() => setLoading(false));
  }, [compraId, navigate]);

  // Função para editar todos os valores unitários
  const handleEditarTodos = () => {
    setModoEdicao(true);
  };

  // Salvar todos os valores unitários editados
  const handleSalvarTodos = () => {
    const valoresInvalidos = itens.some(item => 
      isNaN(item.valorUnitario) || item.valorUnitario <= 0
    );
    if (valoresInvalidos) {
      message.error('Todos os valores unitários devem ser válidos.');
      return;
    }
    setModoEdicao(false);
   
  };

  const handleCancelarEdicao = () => {
    setModoEdicao(false);
    // Recarregar os dados reais da compra
    setLoading(true);
    api.get(API_CONFIG.ENDPOINTS.COMPRAS.VER_ITENS_COMPRA(compraId))
      .then(response => {
        if (response.data && response.data.status && response.data.compras) {
          const compra = response.data.compras;
          setItens(
            (compra.itens || []).map(item => ({
              id: item.IT_ID,
              nome: item.IT_NOME,
              quantidade: Number(item.IT_QUANTIDADE),
              valorUnitario: Number(item.item_compra?.ITC_VALOR_ORIGINAL || 0)
            }))
          );
        }
      })
      .finally(() => setLoading(false));
  };

  const handleValorChange = (idx, novoValor) => {
    const apenasNumeros = novoValor.replace(/[^\d]/g, '');
    if (apenasNumeros === '') {
      const novosItens = [...itens];
      novosItens[idx].valorUnitario = 0;
      setItens(novosItens);
      return;
    }
    const valorNumerico = parseInt(apenasNumeros) / 100;
    const novosItens = [...itens];
    novosItens[idx].valorUnitario = valorNumerico;
    setItens(novosItens);
  };

  const handleAprovarCompra = () => {
    setModalAprovarVisivel(true);
  };

  const handleConfirmarAprovacao = async () => {
    setModalAprovarVisivel(false);
    // Montar payload conforme esperado pela API
    const payload = {
      itens: itens.map(item => ({
        ITC_IT_ID: item.id,
        ITC_VALOR_REVISADO: item.valorUnitario
      }))
    };
    try {
      await api.put(API_CONFIG.ENDPOINTS.COMPRAS.REVISAR_COMPRA(compraId), payload);
      message.success('Compra revisada e enviada com sucesso!');
      navigate('/adm/revisarcotacao');
    } catch (error) {
      message.error('Erro ao enviar revisão da compra!');
    }
  };

  const handleVoltar = () => {
    navigate('/adm/RevisarCotacao');
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60
    },
    {
      title: 'Nome do Item',
      dataIndex: 'nome',
      key: 'nome',
    },
    {
      title: 'Quantidade',
      dataIndex: 'quantidade',
      key: 'quantidade',
      width: 120
    },
    {
      title: 'Valor Unitário (R$)',
      dataIndex: 'valorUnitario',
      key: 'valorUnitario',
      width: 180,
      render: (valor, record, idx) =>
        modoEdicao ? (
          <Input
            value={valor === 0 ? '' : valor.toLocaleString('pt-BR', { 
              minimumFractionDigits: 2, 
              maximumFractionDigits: 2 
            })}
            onChange={e => handleValorChange(idx, e.target.value)}
            style={{ width: 100 }}
            autoFocus={idx === 0}
            placeholder="0,00"
          />
        ) : (
          valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
        )
    },
    {
      title: 'Valor Total (R$)',
      key: 'valorTotal',
      width: 180,
      render: (text, record) => 
        (record.valorUnitario * record.quantidade).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    }
  ];

  if (loading) {
    return <div>Carregando...</div>;
  }

  return (
    <div className="home-container">
      <div className="modal-wrapper">
        <h1 className="modal-title">Revisar Compra</h1>
        <div className="modal-content">
          {/* Cabeçalho do fornecedor */}
          <div className="revisar-pre-header-box">
            <div className="revisar-pre-header-numero">
              <FileTextOutlined style={{ fontSize: 24, color: '#001f4d' }} />
              Fornecedor: <span>{fornecedor.nome}</span>
            </div>
            <div className="revisar-pre-header-info">
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label={<span><CalendarOutlined /> Data de Abertura</span>}>
                  {fornecedor.dataAbertura ? new Date(fornecedor.dataAbertura).toLocaleDateString('pt-BR') : '-'}
                </Descriptions.Item>
              </Descriptions>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label={<span><TagOutlined /> Status</span>}>
                  {fornecedor.status}
                </Descriptions.Item>
              </Descriptions>
            </div>
            <div className="revisar-pre-header-veiculo">
              <span style={{ fontWeight: 600 }}>Veículo:</span> {fornecedor.veiculo}
            </div>
          </div>

          {/* Tabela de itens */}
          <Table
            columns={columns}
            dataSource={itens}
            pagination={false}
            rowKey={record => record.id}
            style={{ background: 'white', marginBottom: 24, clear: 'both' }}
            className="tabela-solicitacao"
          />

          {/* Valor Total */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            alignItems: 'center', 
            marginBottom: 16,
            padding: '12px 16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <span style={{ fontSize: '1.1em', fontWeight: 500, color: '#001f4d', marginRight: '8px' }}>
              Valor Total da Compra:
            </span>
            <span style={{ 
              fontWeight: 'bold', 
              color: '#001f4d', 
              fontSize: '1.3em',
              backgroundColor: '#fff',
              padding: '4px 12px',
              borderRadius: '4px',
              border: '1px solid #001f4d'
            }}>
              {itens.reduce((total, item) => {
                let valor = item.valorUnitario;
                if (typeof valor === 'string') {
                  valor = parseFloat(valor.replace(',', '.'));
                }
                if (isNaN(valor)) valor = 0;
                return total + (valor * item.quantidade);
              }, 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </span>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, gap: 12 }}>
            <Button
              className="editar-submit-button"
              type="primary"
              icon={<ArrowLeftOutlined />}
              onClick={handleVoltar}
              style={{ minWidth: 120, fontWeight: 500, background: '#001f4d', border: 'none' }}
            >
              Voltar
            </Button>
            
            <Space>
              {modoEdicao ? (
                <>
                  <Button type="primary" onClick={handleSalvarTodos} style={{ minWidth: 120 }}>
                    Salvar Valores
                  </Button>
                  <Button onClick={handleCancelarEdicao} style={{ minWidth: 120 }}>
                    Cancelar
                  </Button>
                </>
              ) : (
                <Button onClick={handleEditarTodos} style={{ minWidth: 120 }}>
                  Editar Valores
                </Button>
              )}
              <Button 
                type="primary" 
                style={{ background: '#008026', minWidth: 140 }} 
                onClick={handleAprovarCompra}
              >
                Revisar
              </Button>
            </Space>
          </div>
        </div>
        <Modal
          title="Confirmar Aprovação"
          open={modalAprovarVisivel}
          onOk={handleConfirmarAprovacao}
          onCancel={() => setModalAprovarVisivel(false)}
          okText="Confirmar"
          cancelText="Cancelar"
          width={400}
          centered
          maskClosable={true}
          destroyOnClose={true}
        >
          <p>Tem certeza que deseja aprovar esta compra?</p>
          <p><strong>Valor total:</strong> {itens.reduce((total, item) => total + (item.valorUnitario * item.quantidade), 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
        </Modal>
      </div>
    </div>
  );
};

export default Revisar;